import { useBoolean } from 'ahooks';
import { Tooltip } from 'antd';
import { SeriesColumnOptions } from 'highcharts';
import { keyBy } from 'lodash';
import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  AggregateType,
  CustomCardWidget,
  Field,
  SimpleSearchFilter,
  UnitPosition,
  Widget,
  WidgetDefaultNumberFields,
  WidgetType,
} from 'api/dashboard/widget';
import { Project } from 'api/project';
import { Loading } from 'components/atoms/loading';
import { Filter } from 'components/molecules/filters/types';
import { FilterGroupType } from 'components/pages/types';
import {
  HelperText01,
  ProductiveHeading04,
  ProductiveHeading05,
  SingleLineEllipsis,
} from 'components/styles';
import { useTranslate } from 'hooks/use-translation';
import { useAllFieldsQuery } from 'queries';
import {
  useDrillWidgetMutationV2,
  useResolveMultipleWidgetsQuery,
} from 'queries/widgets';
import { useGetEditWidgetUrlWithSearch } from 'utils/urls';
import { useWidgetFilter } from '../use-widget-filter';
import { useWidgetMenu } from '../use-widget-menu';
import { fieldsOfChart } from './chart-formatting-utils';
import { NumberWidgetCard } from './number-widget-card';
import { formatNumber } from './utils';
import { ErrorState } from './widget-state';

const Content = styled.div`
  cursor: pointer;
`;

const Heading = styled.span`
  ${ProductiveHeading04};
`;

const UnitText = styled.span`
  ${ProductiveHeading05};
`;

const HelperTextWrapper = styled.span`
  ${HelperText01};
`;

const Wrapper = styled.div`
  ${SingleLineEllipsis};
`;

const HourTypes = ['budgeted_hours', 'project_hours'];

const toolTipContent = (
  value: number,
  metaData: CustomCardWidget['metaData'] | null,
): string => {
  let numberContent = value.toLocaleString();

  if (!metaData) {
    return numberContent;
  }

  if ('name' in metaData.field && HourTypes.includes(metaData.field.name)) {
    numberContent = Math.round(value / 3600).toString();
  }

  if (
    'name' in metaData.field &&
    metaData.field.name === WidgetDefaultNumberFields.CSAT
  ) {
    numberContent = value.toFixed(2);
  }

  return numberContent;
};

const modalContent = (
  metaData: CustomCardWidget['metaData'] | null,
  data: number,
): JSX.Element | null => {
  const value = toolTipContent(data, metaData);

  if (!metaData) {
    return <>{`${value} `}</>;
  }

  return (
    <>
      {`${value} `}
      <UnitText>{`${metaData.unitName || ''} `}</UnitText>

      <HelperTextWrapper>
        {metaData.aggType === AggregateType.Total ? (
          <FormattedMessage id="dashboard.custom_charts.label.total" />
        ) : (
          <FormattedMessage id="dashboard.custom_charts.label.avg" />
        )}
      </HelperTextWrapper>
    </>
  );
};

export const getNumberWidgetContent = (
  type: WidgetType,
  value: number,
  unitName = '',
  unitType = '',
  field = {} as Field,
): JSX.Element | null => {
  const numberContent = parseFloat(value.toFixed(2));

  const FormattedNumber = (
    <Heading>
      {`${formatNumber(numberContent).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} `}
    </Heading>
  );
  const TimeInHours = (
    <Heading>{`${Math.round(numberContent / 3600)} `}</Heading>
  );
  const Unit = <UnitText>{`${unitName} `}</UnitText>;

  switch (type) {
    case WidgetType.CustomCard:
      if ('name' in field) {
        if (HourTypes.includes(field.name)) {
          return unitType === UnitPosition.Suffix ? (
            <>
              {TimeInHours}
              {Unit}
            </>
          ) : (
            <>
              {Unit}
              {TimeInHours}
            </>
          );
        }
      }

      return unitType === UnitPosition.Suffix ? (
        <>
          {FormattedNumber}
          {Unit}
        </>
      ) : (
        <>
          {Unit}
          {FormattedNumber}
        </>
      );
    case WidgetType.ARR:
      return (
        <Heading>
          {'$ '}
          {FormattedNumber}
        </Heading>
      );
    case WidgetType.ProjectCount:
      return <Heading>{`${numberContent} `}</Heading>;
    default:
      return null;
  }
};

export const NumberWidget: FC<{
  widget: Widget;
  dashboardFilter: Filter;
  dashboardSimpleFilter: SimpleSearchFilter;
}> = ({ widget, dashboardFilter, dashboardSimpleFilter }) => {
  const { widgetName, widgetId, widgetType, dashboardId } = widget;
  const {
    data: [{ response: chartOptions }] = [{ response: undefined }],
    status,
  } = useResolveMultipleWidgetsQuery(
    dashboardId,
    [widgetId],
    dashboardSimpleFilter,
  );
  const [isVisible, { setTrue, setFalse }] = useBoolean(false);
  const { data: allFields } = useAllFieldsQuery();
  const { mutate, isLoading } = useDrillWidgetMutationV2(widgetId);
  const [data, setData] = useState<Project[]>([]);
  const Menu = useWidgetMenu(widget, dashboardSimpleFilter);
  const filter = useWidgetFilter({
    widget,
    defaultGroup: FilterGroupType.Project,
  });
  const getEditWidgetUrl = useGetEditWidgetUrlWithSearch();
  const translate = useTranslate();
  const fieldMap = useMemo(() => {
    return keyBy(allFields ?? [], 'fieldId');
  }, [allFields]);

  const metaData: CustomCardWidget['metaData'] | null = useMemo(() => {
    return widget.widgetType === WidgetType.CustomCard
      ? (widget as CustomCardWidget).metaData
      : null;
  }, [widget]);

  const cardValue = Number(
    (chartOptions?.series?.[0] as SeriesColumnOptions)?.data?.[0] ?? 0,
  );

  const content = () => {
    switch (status) {
      case 'loading':
        return <Loading />;
      case 'error':
        return (
          <ErrorState
            size="small"
            direction="horizontal"
            message={<FormattedMessage id="dashboard.widget.number.error" />}
            action={
              <Link to={getEditWidgetUrl(dashboardId, widgetId)}>
                <FormattedMessage id="dashboard.edit_chart" />
              </Link>
            }
          />
        );
      case 'success':
        return chartOptions ? (
          (chartOptions.series ?? []).length > 0 ? (
            <Wrapper
              onClick={() => {
                setTrue();
                mutate(
                  { dashboardFilter: dashboardSimpleFilter },
                  {
                    onSuccess: (response) => setData(response),
                  },
                );
              }}
            >
              <Tooltip
                overlayStyle={{ maxWidth: 'unset' }}
                title={toolTipContent(cardValue, metaData)}
              >
                {widgetType === WidgetType.CustomCard
                  ? getNumberWidgetContent(
                      widgetType,
                      cardValue,
                      metaData?.unitName,
                      metaData?.unitType,
                      metaData?.field,
                    )
                  : getNumberWidgetContent(widgetType, cardValue)}
                <HelperTextWrapper>
                  {widgetType === WidgetType.CustomCard
                    ? (widget as CustomCardWidget).metaData.aggType ===
                      AggregateType.Total
                      ? translate('dashboard.custom_charts.label.total')
                      : translate('dashboard.custom_charts.label.avg')
                    : ''}
                </HelperTextWrapper>
              </Tooltip>
            </Wrapper>
          ) : null
        ) : null;
      case 'idle':
      default:
        return null;
    }
  };

  const fieldNames = fieldsOfChart(chartOptions, fieldMap);

  return (
    <NumberWidgetCard
      widgetId={widgetId}
      filter={filter}
      dashboardFilter={dashboardFilter}
      widgetName={widgetName}
      widgetMenu={Menu}
      loading={isLoading}
      preview={false}
      content={<Content>{content()}</Content>}
      modalContent={modalContent(metaData, cardValue)}
      listData={data}
      setData={setData}
      closeModal={setFalse}
      isModalVisible={isVisible}
      fieldNames={fieldNames}
      dashboardSimpleFilter={dashboardSimpleFilter}
    />
  );
};
